import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { localSongs } from '@/content/mobile';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/app/songs/$slug')({ component: SongPage });

function SongPage() {
  const { slug } = Route.useParams();
  const song = localSongs[slug as keyof typeof localSongs];
  const audio = useRef<HTMLAudioElement>(null);
  const activeWord = useRef<HTMLSpanElement>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [translation, setTranslation] = useState(false);
  useEffect(() => { track('Song Screen', { song: slug }); }, [slug]);
  useEffect(() => {
    activeWord.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [time]);
  if (!song) return <main className="grid min-h-[75vh] place-items-center">Song not found.</main>;
  const lyricRows = song.lyrics.lyrics;
  const togglePlay = () => {
    const node = audio.current;
    if (!node) return;
    if (node.paused) {
      void node.play();
      track('Song Play', { song: slug });
    } else {
      node.pause();
    }
  };
  const restart = () => {
    const node = audio.current;
    if (!node) return;
    node.currentTime = 0;
    void node.play();
  };
  return (
    <div className="mx-auto max-w-4xl px-5 pb-40 pt-8 sm:px-8 lg:pt-12">
      <Link to="/app/resources" className="font-bold text-[#2d7a13]">← Learning Hub</Link>
      <h1 className="mt-8 font-nunito text-2xl font-bold text-[#1E1E1E]">{song.title}</h1>
      <p className="mt-2 text-slate-500">Written and sung by {song.author}</p>
      <audio
        ref={audio}
        src={song.audio}
        loop={loop}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration * 1000)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime * 1000)}
        className="hidden"
      />
      <p className="mt-8 font-nunito text-[28px] font-black leading-[34px]">
        {lyricRows.map((lyric, index) => {
          const active = time >= lyric.start && time < lyric.end;
          return (
            <span
              key={`${lyric.start}-${index}`}
              ref={active ? activeWord : undefined}
              className={active ? 'rounded bg-[#fff0a8] px-1 text-slate-950' : ''}
            >
              {lyric.word}{' '}{lyric.lineBreak ? <br /> : null}
            </span>
          );
        })}
      </p>

      {translation ? (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-5" role="dialog" aria-label="English translation">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-black">{song.title}</h2>
            <p className="mt-1 text-sm text-slate-500">(English Translation)</p>
            <p className="mt-4 whitespace-pre-line leading-7 text-slate-700">{song.englishTranslation}</p>
            <button type="button" onClick={() => setTranslation(false)} className="mt-6 rounded-xl bg-[#399918] px-4 py-2 font-bold text-white">Close</button>
          </div>
        </div>
      ) : null}

      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-[#cfe8c6] bg-[#ECFEE6] px-5 py-3 lg:bottom-0">
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          <div className="flex items-center justify-between text-xs font-bold text-slate-500">
            <span>{formatSongClock(time)}</span>
            <span>{formatSongClock(duration)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white">
            <div className="h-full bg-[#399918]" style={{ width: duration ? `${Math.min(100, (time / duration) * 100)}%` : '0%' }} />
          </div>
          <div className="flex items-center justify-center gap-3">
            <ControlButton label={playing ? 'Pause' : 'Play'} onClick={togglePlay}>{playing ? '❚❚' : '▶'}</ControlButton>
            <ControlButton label="Restart" onClick={restart}>↺</ControlButton>
            <ControlButton label="Loop" onClick={() => setLoop((value) => !value)} active={loop}>∞</ControlButton>
            <ControlButton label="English translation" onClick={() => setTranslation(true)}>EN</ControlButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatSongClock(ms: number) {
  if (!ms) return '0:00.000';
  const total = Math.floor(ms);
  const minutes = Math.floor(total / 1000 / 60);
  const seconds = Math.floor(total / 1000) % 60;
  const millis = total % 1000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function ControlButton({
  children,
  label,
  onClick,
  active,
}: React.PropsWithChildren<{ label: string; onClick: () => void; active?: boolean }>) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`grid h-12 w-12 place-items-center rounded-2xl text-lg font-black text-white ${active ? 'bg-[#2d7a13]' : 'bg-[#399918]'}`}
    >
      {children}
    </button>
  );
}
